import React, { useState, useEffect, useCallback } from 'react';
import { X, Calendar, Plus, Trash2, Edit2, Loader2, Save, Award, CheckCircle2, Info, Clock } from 'lucide-react';
import { apiClient } from '../utils/apiClient';
import CustomDatePicker from './common/CustomDatePicker';

export default function EmploymentHistoryModal({ isOpen, onClose, employeeId, employeeName, onUpdated }) {
    const [periods, setPeriods] = useState([]);
    const [senioritySummary, setSenioritySummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Form State
    const [isEditing, setIsEditing] = useState(false);
    const [currentPeriod, setCurrentPeriod] = useState({
        id: null,
        startDate: '',
        endDate: '',
        terminationReason: '',
        includeInSeniority: true,
        isLiquidated: false
    });

    const fetchPeriodsAndSummary = useCallback(async () => {
        if (!employeeId) return;
        try {
            setLoading(true);
            setError(null);
            const [periodsData, summaryData] = await Promise.all([
                apiClient(`Employee/${employeeId}/employment-periods`),
                apiClient(`Employee/${employeeId}/seniority-summary`).catch(() => null)
            ]);
            setPeriods(periodsData || []);
            setSenioritySummary(summaryData || null);
        } catch (err) {
            setError(err.message || 'Çalışma geçmişi yüklenirken hata oluştu.');
        } finally {
            setLoading(false);
        }
    }, [employeeId]);

    useEffect(() => {
        if (isOpen && employeeId) {
            fetchPeriodsAndSummary();
        } else {
            setPeriods([]);
            setSenioritySummary(null);
            setIsEditing(false);
            setCurrentPeriod({
                id: null,
                startDate: '',
                endDate: '',
                terminationReason: '',
                includeInSeniority: true,
                isLiquidated: false
            });
            setError(null);
        }
    }, [isOpen, employeeId, fetchPeriodsAndSummary]);

    const toLocalISOString = (date) => {
        if (!date) return null;
        if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
        const d = new Date(date);
        const tzOffset = d.getTimezoneOffset() * 60000;
        return new Date(d.getTime() - tzOffset).toISOString().slice(0, 10);
    };

    const handleSave = async () => {
        try {
            if (!currentPeriod.startDate || !currentPeriod.endDate) {
                setError('İşe Giriş ve Çıkış tarihleri zorunludur.');
                return;
            }

            const startIso = toLocalISOString(currentPeriod.startDate);
            const endIso = toLocalISOString(currentPeriod.endDate);

            if (new Date(endIso) < new Date(startIso)) {
                setError('Çıkış tarihi, giriş tarihinden önce olamaz.');
                return;
            }

            setLoading(true);
            const payload = {
                startDate: startIso,
                endDate: endIso,
                terminationReason: currentPeriod.terminationReason || null,
                includeInSeniority: Boolean(currentPeriod.includeInSeniority),
                isLiquidated: Boolean(currentPeriod.isLiquidated)
            };

            if (currentPeriod.id) {
                await apiClient(`Employee/${employeeId}/employment-periods/${currentPeriod.id}`, {
                    method: 'PUT',
                    body: payload
                });
            } else {
                await apiClient(`Employee/${employeeId}/employment-periods`, {
                    method: 'POST',
                    body: payload
                });
            }

            await fetchPeriodsAndSummary();
            if (onUpdated) onUpdated();
            setIsEditing(false);
            setCurrentPeriod({
                id: null,
                startDate: '',
                endDate: '',
                terminationReason: '',
                includeInSeniority: true,
                isLiquidated: false
            });
            setError(null);
        } catch (err) {
            setError(err.message || 'Kayıt sırasında hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Bu çalışma dönemini silmek istediğinize emin misiniz? Kıdem tarihi otomatik yeniden hesaplanacaktır.')) return;
        try {
            setLoading(true);
            await apiClient(`Employee/${employeeId}/employment-periods/${id}`, { method: 'DELETE' });
            await fetchPeriodsAndSummary();
            if (onUpdated) onUpdated();
        } catch (err) {
            setError(err.message || 'Silme işlemi sırasında hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    const calcDaysBetween = (start, end) => {
        if (!start || !end) return 0;
        const s = new Date(start);
        const e = new Date(end);
        s.setHours(0, 0, 0, 0);
        e.setHours(0, 0, 0, 0);
        const diff = Math.round((e - s) / (1000 * 60 * 60 * 24)) + 1;
        return diff > 0 ? diff : 0;
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white dark:bg-gray-900 w-full max-w-3xl rounded-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden border border-gray-200 dark:border-gray-800">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center bg-gray-50/50 dark:bg-gray-800/30">
                    <div className="flex items-center text-indigo-600 dark:text-indigo-400">
                        <Calendar className="mr-3" size={24} />
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Çalışma Geçmişi ve Kıdem Birleştirme</h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                                {employeeName || 'Personel'} — 4857 Sayılı İş Kanunu Md. 54 Kıdem & Yıllık İzin Entegrasyonu
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors">
                        <X size={24} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-5">
                    {/* Canlı Kıdem ve İzin Hakediş Özeti Kartı */}
                    {senioritySummary && (
                        <div className="rounded-xl border border-indigo-200 dark:border-indigo-800/60 bg-gradient-to-br from-indigo-50/80 via-white to-purple-50/60 dark:from-indigo-950/40 dark:via-gray-900 dark:to-purple-950/30 p-4 shadow-sm">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                    <Award className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                                    <span className="text-xs font-bold uppercase tracking-wider text-indigo-900 dark:text-indigo-200">
                                        Otomatik Hesaplanan Kıdem & İzin Özeti
                                    </span>
                                </div>
                                <span className="text-[11px] px-2.5 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-900/60 text-indigo-700 dark:text-indigo-300 font-semibold">
                                    Kademe: {senioritySummary.effectiveTierYears || 0}. Yıl
                                </span>
                            </div>

                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                                <div className="bg-white/80 dark:bg-gray-800/70 rounded-lg p-2.5 border border-gray-100 dark:border-gray-700/60">
                                    <span className="text-gray-500 dark:text-gray-400 block text-[11px]">Güncel İşe Giriş</span>
                                    <span className="font-bold text-gray-900 dark:text-white font-mono">
                                        {senioritySummary.hireDate ? new Date(senioritySummary.hireDate).toLocaleDateString('tr-TR') : '-'}
                                    </span>
                                </div>

                                <div className="bg-white/80 dark:bg-gray-800/70 rounded-lg p-2.5 border border-emerald-200/70 dark:border-emerald-800/50">
                                    <span className="text-gray-500 dark:text-gray-400 block text-[11px]">Efektif Kıdem Tarihi</span>
                                    <span className="font-bold text-emerald-700 dark:text-emerald-400 font-mono">
                                        {senioritySummary.effectiveSeniorityDate
                                            ? new Date(senioritySummary.effectiveSeniorityDate).toLocaleDateString('tr-TR')
                                            : '-'}
                                    </span>
                                    {senioritySummary.unliquidatedSeniorityDays > 0 && (
                                        <span className="block text-[10px] text-emerald-600 dark:text-emerald-400 mt-0.5">
                                            ({senioritySummary.unliquidatedSeniorityDays} gün geri çekildi)
                                        </span>
                                    )}
                                </div>

                                <div className="bg-white/80 dark:bg-gray-800/70 rounded-lg p-2.5 border border-amber-200/70 dark:border-amber-800/50">
                                    <span className="text-gray-500 dark:text-gray-400 block text-[11px]">Tasfiye Edilmiş Kıdem</span>
                                    <span className="font-bold text-amber-700 dark:text-amber-400 font-mono">
                                        +{senioritySummary.previousLiquidatedYears || 0} Yıl
                                    </span>
                                    {senioritySummary.liquidatedSeniorityDays > 0 && (
                                        <span className="block text-[10px] text-amber-600 dark:text-amber-400 mt-0.5">
                                            ({senioritySummary.liquidatedSeniorityDays} gün geçmiş)
                                        </span>
                                    )}
                                </div>

                                <div className="bg-white/80 dark:bg-gray-800/70 rounded-lg p-2.5 border border-purple-200/70 dark:border-purple-800/50">
                                    <span className="text-gray-500 dark:text-gray-400 block text-[11px]">Sonraki Hakediş Tarihi</span>
                                    <span className="font-bold text-purple-700 dark:text-purple-300 font-mono">
                                        {senioritySummary.nextAnniversaryDate
                                            ? new Date(senioritySummary.nextAnniversaryDate).toLocaleDateString('tr-TR')
                                            : '-'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-300 p-3 rounded-lg text-sm font-medium border border-red-100 dark:border-red-800">
                            {error}
                        </div>
                    )}

                    {isEditing ? (
                        <div className="bg-indigo-50/50 dark:bg-indigo-900/10 p-5 rounded-xl border border-indigo-100 dark:border-indigo-800/30 space-y-4">
                            <h3 className="font-semibold text-indigo-900 dark:text-indigo-300 border-b border-indigo-100 dark:border-indigo-800/50 pb-2">
                                {currentPeriod.id ? 'Çalışma Dönemini Düzenle' : 'Yeni Geçmiş Çalışma Dönemi Ekle'}
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Giriş Tarihi *</label>
                                    <CustomDatePicker
                                        selectedDate={currentPeriod.startDate}
                                        onDateChange={(d) => setCurrentPeriod({ ...currentPeriod, startDate: d })}
                                        placeholder="GG.AA.YYYY"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Çıkış Tarihi *</label>
                                    <CustomDatePicker
                                        selectedDate={currentPeriod.endDate}
                                        onDateChange={(d) => setCurrentPeriod({ ...currentPeriod, endDate: d })}
                                        placeholder="GG.AA.YYYY"
                                    />
                                </div>
                            </div>

                            {/* Kıdem ve Tasfiye Seçenekleri */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                                <label className="flex items-start gap-2.5 p-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 cursor-pointer hover:border-indigo-400 transition-colors">
                                    <input
                                        type="checkbox"
                                        checked={currentPeriod.includeInSeniority}
                                        onChange={(e) => setCurrentPeriod({ ...currentPeriod, includeInSeniority: e.target.checked })}
                                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                    />
                                    <div className="text-xs">
                                        <span className="font-semibold text-gray-900 dark:text-gray-100 block">
                                            Kıdem ve İzin Hesabına Dahil Et
                                        </span>
                                        <span className="text-gray-500 dark:text-gray-400 text-[11px]">
                                            4857 Md. 54 gereği bu dönem yıllık izin kıdeminde birleştirilir.
                                        </span>
                                    </div>
                                </label>

                                <label className={`flex items-start gap-2.5 p-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 cursor-pointer hover:border-amber-400 transition-colors ${!currentPeriod.includeInSeniority ? 'opacity-50 pointer-events-none' : ''}`}>
                                    <input
                                        type="checkbox"
                                        checked={currentPeriod.isLiquidated}
                                        disabled={!currentPeriod.includeInSeniority}
                                        onChange={(e) => setCurrentPeriod({ ...currentPeriod, isLiquidated: e.target.checked })}
                                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                                    />
                                    <div className="text-xs">
                                        <span className="font-semibold text-gray-900 dark:text-gray-100 block">
                                            İzin/Kıdem Hakları Tasfiye Edildi (Ödendi)
                                        </span>
                                        <span className="text-gray-500 dark:text-gray-400 text-[11px]">
                                            İşaretlenirse yıl dönümü değişmez, sadece izin kademesi (14→20→26) yükselir.
                                        </span>
                                    </div>
                                </label>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Çıkış Nedeni / Not (Opsiyonel)</label>
                                <textarea
                                    value={currentPeriod.terminationReason || ''}
                                    onChange={(e) => setCurrentPeriod({ ...currentPeriod, terminationReason: e.target.value })}
                                    rows={2}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white text-sm"
                                    placeholder="Örn: İstifa, Askerlik, Grup şirketi devri..."
                                />
                            </div>
                            <div className="flex justify-end space-x-2 pt-2">
                                <button
                                    onClick={() => { setIsEditing(false); setError(null); }}
                                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors font-medium text-sm"
                                >
                                    İptal
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={loading}
                                    className="flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors shadow-sm font-medium text-sm disabled:opacity-50"
                                >
                                    {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
                                    Kaydet ve Kıdemi Güncelle
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                                <Info size={14} className="text-indigo-500 shrink-0" />
                                <span>Eklenen dönemler personelin Kıdem Başlangıç Tarihini ve İzin Kademesini otomatik günceller.</span>
                            </div>
                            <button
                                onClick={() => {
                                    setIsEditing(true);
                                    setCurrentPeriod({
                                        id: null,
                                        startDate: '',
                                        endDate: '',
                                        terminationReason: '',
                                        includeInSeniority: true,
                                        isLiquidated: false
                                    });
                                }}
                                className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors font-semibold text-xs shadow-sm shrink-0"
                            >
                                <Plus size={15} className="mr-1.5" /> Yeni Dönem Ekle
                            </button>
                        </div>
                    )}

                    {loading && !isEditing ? (
                        <div className="flex justify-center py-8">
                            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                        </div>
                    ) : periods.length === 0 && !isEditing ? (
                        <div className="text-center py-10 bg-gray-50 dark:bg-gray-800/30 rounded-xl border border-dashed border-gray-200 dark:border-gray-700">
                            <Calendar size={36} className="mx-auto text-gray-300 dark:text-gray-600 mb-2" />
                            <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">Bu personele ait geçmiş çalışma dönemi bulunamadı.</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                Personel daha önce firmanızda çalıştıysa dönem ekleyerek kıdemini otomatik birleştirebilirsiniz.
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {periods.map(period => {
                                const days = period.totalDays || calcDaysBetween(period.startDate, period.endDate);
                                const approxYears = (days / 365).toFixed(1);
                                const incSeniority = period.includeInSeniority !== false;
                                const isLiq = Boolean(period.isLiquidated);

                                return (
                                    <div key={period.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 flex items-start justify-between shadow-sm hover:border-indigo-300 dark:hover:border-indigo-700 transition-colors">
                                        <div className="space-y-1.5">
                                            <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
                                                <Calendar size={14} className="text-indigo-500" />
                                                <span>
                                                    {new Date(period.startDate).toLocaleDateString('tr-TR')} — {new Date(period.endDate).toLocaleDateString('tr-TR')}
                                                </span>
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-indigo-50 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300">
                                                    <Clock size={11} /> {days} gün (~{approxYears} yıl)
                                                </span>
                                                {incSeniority ? (
                                                    isLiq ? (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300">
                                                            Tasfiye Edildi (Sadece Kademe Artırır)
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300">
                                                            <CheckCircle2 size={11} /> Kıdem Tarihini Geri Çeker
                                                        </span>
                                                    )
                                                ) : (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                                        Kıdem Dışı
                                                    </span>
                                                )}
                                            </div>
                                            {period.terminationReason && (
                                                <p className="text-xs text-gray-500 dark:text-gray-400 pl-5">
                                                    <span className="font-medium">Çıkış Nedeni:</span> {period.terminationReason}
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex space-x-1 shrink-0 ml-2">
                                            <button
                                                onClick={() => {
                                                    setIsEditing(true);
                                                    setCurrentPeriod({
                                                        ...period,
                                                        includeInSeniority: period.includeInSeniority !== false,
                                                        isLiquidated: Boolean(period.isLiquidated)
                                                    });
                                                }}
                                                className="p-1.5 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded transition-colors"
                                                title="Düzenle"
                                            >
                                                <Edit2 size={16} />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(period.id)}
                                                className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
                                                title="Sil"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
